import { getDb, type CatalogGroup, type CatalogItem } from './db.ts';
import { newId } from '../shared/id.ts';

export async function listGroups(): Promise<CatalogGroup[]> {
  const db = await getDb();
  const groups = await db.getAll('groups');
  return groups.sort((a, b) => a.sortIndex - b.sortIndex);
}

export async function getGroup(id: string): Promise<CatalogGroup | undefined> {
  const db = await getDb();
  return db.get('groups', id);
}

export async function listItems(groupId: string): Promise<CatalogItem[]> {
  const db = await getDb();
  const items = await db.getAllFromIndex('items', 'byGroup', groupId);
  return items.sort((a, b) => a.sortIndex - b.sortIndex);
}

export async function getItem(id: string): Promise<CatalogItem | undefined> {
  const db = await getDb();
  return db.get('items', id);
}

export async function listAllItems(): Promise<CatalogItem[]> {
  const db = await getDb();
  return db.getAll('items');
}

export async function createGroup(title: string, imageUrl: string): Promise<CatalogGroup> {
  const db = await getDb();
  const existing = await db.getAll('groups');
  const group: CatalogGroup = {
    id: newId(),
    title,
    imageUrl,
    sortIndex: existing.length,
  };
  await db.put('groups', group);
  return group;
}

export async function saveGroup(group: CatalogGroup): Promise<void> {
  const db = await getDb();
  await db.put('groups', group);
}

/** Loescht eine Gruppe samt aller enthaltenen Folgen. */
export async function deleteGroup(groupId: string): Promise<void> {
  const db = await getDb();
  const items = await db.getAllFromIndex('items', 'byGroup', groupId);
  const tx = db.transaction(['groups', 'items'], 'readwrite');
  await Promise.all([
    tx.objectStore('groups').delete(groupId),
    ...items.map((item) => tx.objectStore('items').delete(item.id)),
    tx.done,
  ]);
}

export async function addItem(
  item: Omit<CatalogItem, 'sortIndex'> & { sortIndex?: number },
): Promise<void> {
  const db = await getDb();
  const siblings = await db.getAllFromIndex('items', 'byGroup', item.groupId);
  await db.put('items', { ...item, sortIndex: item.sortIndex ?? siblings.length });
  await refreshGroupImage(item.groupId);
}

export async function deleteItem(itemId: string): Promise<void> {
  const db = await getDb();
  const item = await db.get('items', itemId);
  await db.delete('items', itemId);
  if (item) await refreshGroupImage(item.groupId);
}

/** Verschiebt eine Folge in eine andere Gruppe und haengt sie dort hinten an. */
export async function moveItem(itemId: string, targetGroupId: string): Promise<void> {
  const db = await getDb();
  const item = await db.get('items', itemId);
  if (!item || item.groupId === targetGroupId) return;

  const siblings = await db.getAllFromIndex('items', 'byGroup', targetGroupId);
  await db.put('items', { ...item, groupId: targetGroupId, sortIndex: siblings.length });

  // Beide Seiten pruefen: die Quelle verliert womoeglich ihr Bild,
  // die Zielgruppe hat vielleicht noch keins.
  await refreshGroupImage(item.groupId);
  await refreshGroupImage(targetGroupId);
}

/**
 * Haelt das Gruppenbild gueltig.
 *
 * Ein automatisch uebernommenes Bild wird ersetzt, sobald die Folge, aus der
 * es stammt, nicht mehr in der Gruppe liegt. Von den Eltern selbst gesetzte
 * Bilder bleiben unangetastet.
 */
export async function refreshGroupImage(groupId: string): Promise<void> {
  const db = await getDb();
  const group = await db.get('groups', groupId);
  if (!group || group.imageManual) return;

  const items = await db.getAllFromIndex('items', 'byGroup', groupId);
  const stillPresent =
    group.imageItemId && items.some((item) => item.id === group.imageItemId);
  if (stillPresent && group.imageUrl) return;

  const source = items.find((item) => item.imageUrl);
  await db.put('groups', {
    ...group,
    imageUrl: source?.imageUrl ?? '',
    imageItemId: source?.id,
  });
}

/** Setzt ein bewusst gewaehltes Gruppenbild, das nicht automatisch wechselt. */
export async function setGroupImageManual(groupId: string, imageUrl: string): Promise<void> {
  const db = await getDb();
  const group = await db.get('groups', groupId);
  if (!group) return;
  await db.put('groups', { ...group, imageUrl, imageManual: true, imageItemId: undefined });
}

/** Schaltet zurueck auf automatische Bildwahl aus den Folgen der Gruppe. */
export async function setGroupImageAuto(groupId: string): Promise<void> {
  const db = await getDb();
  const group = await db.get('groups', groupId);
  if (!group) return;
  await db.put('groups', { ...group, imageManual: false, imageItemId: undefined, imageUrl: '' });
  await refreshGroupImage(groupId);
}

/** Prueft, ob eine Folge bereits irgendwo im Katalog liegt. */
export async function findItemGroup(itemId: string): Promise<string | null> {
  const db = await getDb();
  const item = await db.get('items', itemId);
  return item?.groupId ?? null;
}
